'use client';
import { animated, config, useTransition } from '@react-spring/web';
import classNames from 'classnames';
import constant from 'lodash/constant';
import isFunction from 'lodash/isFunction';
import noop from 'lodash/noop';
import uniqueId from 'lodash/uniqueId';
import {
  type ComponentType,
  createContext,
  memo,
  type ReactNode,
  useCallback,
  useContext,
  useMemo,
  useRef,
  useState,
} from 'react';

import { Portal } from '@air/primitive-portal';

import { getToastListStyle } from './getToastListStyle';
import { ToastItem, type ToastItemProps, type ToastPlacement } from './ToastItem';

export type { ToastPlacement } from './ToastItem';

export type ToastItemPropsType = Omit<ToastItemProps, 'children' | 'onDismiss'>;

/**
 * Minimal props interface for custom toast containers.
 * Any component that accepts these props can be used as a CustomContainer.
 */
export type CustomToastContainerProps = {
  children: ReactNode;
  onDismiss: () => void;
  timeBeforeAutoDismiss?: number | null;
  type?: 'assertive' | 'polite';
};

export type ToastOptions =
  | ({ onDismiss?: () => void; placement?: ToastPlacement } & ToastItemPropsType & { CustomContainer?: never })
  | ({ onDismiss?: () => void; placement?: ToastPlacement } & {
      CustomContainer: ComponentType<CustomToastContainerProps>;
    } & Omit<CustomToastContainerProps, 'children' | 'onDismiss'>);

export type Toast = {
  id: string;
  content: (({ onDismiss }: { onDismiss: () => void }) => ReactNode) | ReactNode;
  options?: ToastOptions;
};

const createToast = (content: Toast['content'], options?: ToastOptions): Toast => ({
  content,
  id: uniqueId('toast'),
  options,
});

const toastOptionDefaults: ToastOptions = {
  color: 'black',
  onDismiss: noop,
  prefix: null,
  suffix: null,
  timeBeforeAutoDismiss: 5_000,
  type: 'polite',
  withCloseButton: false,
};

const TOAST_SLIDE_OFFSET_PX = 16;

const getToastPlacement = (toast: Toast): ToastPlacement => toast.options?.placement ?? 'bottom';

type ToastContextValue = {
  showToast: (content: Toast['content'], options?: ToastOptions) => string;
  dismissToast: (id: string) => void;
  dismissAllToasts: () => void;
  setToastBottomOffset: (offset: number) => void;
  setToastXOffset: (offset: number | string) => void;
  getToastXOffset: () => number | string;
  setToastRightOffset: (offset: number | string) => void;
  getToastRightOffset: () => number | string;
  setToastContainer: (el: HTMLElement | null) => void;
};

const defaultValue: ToastContextValue = {
  dismissAllToasts: noop,
  dismissToast: noop,
  getToastRightOffset: constant(0),
  getToastXOffset: constant(0),
  setToastBottomOffset: noop,
  setToastContainer: noop,
  setToastRightOffset: noop,
  setToastXOffset: noop,
  showToast: constant(''),
};

const ToastContext = createContext(defaultValue);

export type ToastProviderProps = {
  children: ReactNode;
  /**
   * When provided, toasts render inside this container with absolute positioning
   * instead of rendering to document.body with fixed positioning.
   * The container element should have `position: relative`.
   */
  container?: HTMLElement | null;
  /**
   * Allows you to customize the rendering of the toast.
   */
  renderToast?: (
    toast: ToastOptions & {
      children?: ReactNode;
    },
  ) => ReactNode;
};

export type ToastListProps = {
  bottomOffset: number;
  clearToast: (id: string) => void;
  container: HTMLElement | null | undefined;
  placement: ToastPlacement;
  renderToast?: ToastProviderProps['renderToast'];
  toastRightOffset: number | string;
  toastXOffset: number | string;
  toasts: Toast[];
};

type ToastListItemProps = {
  clearToast: (id: string) => void;
  isTop: boolean;
  item: Toast;
  renderToast?: ToastProviderProps['renderToast'];
  style: object;
};

const ToastListItem = memo(({ clearToast, isTop, item, renderToast, style }: ToastListItemProps) => {
  const { color, onDismiss, prefix, suffix, timeBeforeAutoDismiss, withCloseButton, type, CustomContainer } = {
    ...toastOptionDefaults,
    ...item.options,
  };

  const dismissHandler = useCallback(() => {
    onDismiss?.();
    clearToast(item.id);
  }, [clearToast, item.id, onDismiss]);

  const children = isFunction(item.content) ? item.content({ onDismiss: dismissHandler }) : item.content;

  let toastContent: ReactNode;

  if (renderToast) {
    toastContent = renderToast({
      children,
      color,
      onDismiss: dismissHandler,
      prefix,
      suffix,
      timeBeforeAutoDismiss,
      type,
      withCloseButton,
    });
  } else if (CustomContainer) {
    toastContent = (
      <CustomContainer onDismiss={dismissHandler} timeBeforeAutoDismiss={timeBeforeAutoDismiss} type={type}>
        {children}
      </CustomContainer>
    );
  } else {
    toastContent = (
      <ToastItem
        color={color}
        onDismiss={dismissHandler}
        prefix={prefix}
        suffix={suffix}
        timeBeforeAutoDismiss={timeBeforeAutoDismiss}
        type={type}
        withCloseButton={withCloseButton}
      >
        {children}
      </ToastItem>
    );
  }

  return (
    <animated.li className={classNames('pointer-events-auto list-none', isTop && 'w-full')} style={style}>
      {toastContent}
    </animated.li>
  );
});

ToastListItem.displayName = 'ToastListItem';

const ToastList = memo(
  ({
    bottomOffset,
    clearToast,
    container,
    placement,
    renderToast,
    toastRightOffset,
    toastXOffset,
    toasts,
  }: ToastListProps) => {
    const slideOffset = placement === 'top' ? -TOAST_SLIDE_OFFSET_PX : TOAST_SLIDE_OFFSET_PX;

    const transitions = useTransition(toasts, {
      config: config.stiff,
      enter: {
        opacity: 1,
        y: 0,
      },
      from: {
        opacity: 0,
        y: slideOffset,
      },
      keys: (item) => item.id,
      leave: {
        opacity: 0,
        y: slideOffset,
      },
    });

    if (!toasts.length) {
      return null;
    }

    const isTop = placement === 'top';
    const listStyle = getToastListStyle({ bottomOffset, container, placement, toastRightOffset, toastXOffset });

    return (
      <ul
        className={classNames(
          'pointer-events-none z-portal m-0 flex flex-col px-3 py-3',
          container
            ? classNames('absolute right-0 left-0', isTop ? 'top-0 items-stretch' : 'bottom-0 items-center')
            : classNames('fixed right-0 left-0', isTop ? 'top-0 items-stretch' : 'bottom-0 items-center'),
        )}
        data-testid={isTop ? 'TOAST_LIST_TOP' : 'TOAST_LIST'}
        style={listStyle}
      >
        {transitions((transitionStyle, item) => (
          <ToastListItem
            clearToast={clearToast}
            isTop={isTop}
            item={item}
            key={item.id}
            renderToast={renderToast}
            style={transitionStyle}
          />
        ))}
      </ul>
    );
  },
);

ToastList.displayName = 'ToastList';

export const ToastProvider = memo(({ children, container: containerProp, renderToast }: ToastProviderProps) => {
  const [toasts, setToasts] = useState<Toast[]>([]);
  const [bottomOffset, setToastBottomOffset] = useState<number>(0);
  const [internalContainer, setToastContainer] = useState<HTMLElement | null>(null);
  const toastXOffsetRef = useRef<number | string>(0);
  const toastRightOffsetRef = useRef<number | string>(0);
  const [toastXOffset, setToastXOffsetState] = useState<number | string>(0);
  const [toastRightOffset, setToastRightOffsetState] = useState<number | string>(0);
  const container = internalContainer ?? containerProp;
  const dismissAllToasts = useCallback(() => {
    // do not set an empty array if there are no toast to avoid unnecessary rerenders on route change
    setToasts((currentToasts) => (currentToasts.length === 0 ? currentToasts : []));
  }, []);

  const clearToast = useCallback((id: string) => {
    setToasts((current) => current.filter((t) => t.id !== id));
  }, []);

  const value: ToastContextValue = useMemo(
    () => ({
      dismissAllToasts,
      dismissToast: clearToast,
      getToastRightOffset: () => toastRightOffsetRef.current,
      getToastXOffset: () => toastXOffsetRef.current,
      setToastBottomOffset,
      setToastContainer,
      setToastRightOffset: (offset: number | string) => {
        toastRightOffsetRef.current = offset;
        setToastRightOffsetState(offset);
      },
      setToastXOffset: (offset: number | string) => {
        toastXOffsetRef.current = offset;
        setToastXOffsetState(offset);
      },
      showToast: (content, options) => {
        const toast = createToast(content, options);
        setToasts((currentToasts) => [...currentToasts, toast]);
        return toast.id;
      },
    }),
    [clearToast, dismissAllToasts],
  );

  const topToasts = useMemo(() => toasts.filter((toast) => getToastPlacement(toast) === 'top'), [toasts]);
  const bottomToasts = useMemo(() => toasts.filter((toast) => getToastPlacement(toast) === 'bottom'), [toasts]);

  return (
    <ToastContext.Provider value={value}>
      {children}
      {!!toasts.length && (
        <Portal container={container}>
          <ToastList
            bottomOffset={bottomOffset}
            clearToast={clearToast}
            container={container}
            placement="top"
            renderToast={renderToast}
            toastRightOffset={toastRightOffset}
            toasts={topToasts}
            toastXOffset={toastXOffset}
          />
          <ToastList
            bottomOffset={bottomOffset}
            clearToast={clearToast}
            container={container}
            placement="bottom"
            renderToast={renderToast}
            toastRightOffset={toastRightOffset}
            toasts={bottomToasts}
            toastXOffset={toastXOffset}
          />
        </Portal>
      )}
    </ToastContext.Provider>
  );
});

ToastProvider.displayName = 'ToastProvider';

export const useToasts = () => {
  const context = useContext(ToastContext);

  if (context === defaultValue) {
    throw new Error('useToasts used outside of ToastProvider');
  }

  return context;
};
